import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, LogOut, ShoppingBag, Sparkles } from "lucide-react";
import { LogoMark } from "@/components/brand/logo";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CartSheet } from "@/features/reseller/components/cart-sheet";
import { ROLE_META, ROLE_HOME } from "@/lib/roles";
import { ROLE_ICON } from "@/lib/nav";
import { useCart } from "@/lib/cart";
import { useAuthStore } from "@/stores/auth.store";
import { useDemoMode } from "@/lib/demo-mode";

/** Sticky top bar. On mobile it carries the brand; on desktop, the role switcher. */
export function AppHeader({ title }: { title?: string }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const demoMode = useDemoMode((s) => s.demoMode);
  const cartCount = useCart((s) => s.items.length);
  const refreshCart = useCart((s) => s.refresh);
  const [cartOpen, setCartOpen] = useState(false);

  // Load held reservations from the server when a reseller mounts (live mode).
  useEffect(() => {
    if (user?.role === "reseller") void refreshCart();
  }, [user?.role, refreshCart]);

  if (!user) return null;

  const switchRole = (roleId: string) => {
    const meta = ROLE_META.find((r) => r.role === roleId);
    if (!meta) return;
    login(meta.demoUser);
    navigate(ROLE_HOME[roleId]);
  };

  return (
    <header
      className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur-sm sm:px-6"
      style={{ scrollPaddingTop: "4rem" }}
    >
      <div className="flex items-center gap-2 lg:hidden">
        <LogoMark className="size-7" />
        <span className="text-base font-bold tracking-tight">
          Stock<span className="text-primary">Sync</span>
        </span>
      </div>

      {title && (
        <h1 className="hidden truncate text-lg font-semibold lg:block">{title}</h1>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        {demoMode && (
          <Badge variant="warning" className="hidden gap-1 sm:inline-flex">
            <Sparkles className="size-3" /> Demo data
          </Badge>
        )}

        {/* Reserved cart — resellers only */}
        {user.role === "reseller" && (
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setCartOpen(true)}
            aria-label={`Reserved cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`}
          >
            <ShoppingBag className="size-5" />
            {cartCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex size-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums">
                {cartCount}
              </span>
            )}
          </Button>
        )}

        <ThemeToggle />

        {/* Demo role switcher — lets a viewer hop between the three portals. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5">
              {(() => {
                const Icon = ROLE_ICON[user.role];
                return <Icon className="size-4 text-primary" />;
              })()}
              <span className="hidden sm:inline">{roleLabel(user.role)}</span>
              <ChevronsUpDown className="size-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              View as (demo)
            </DropdownMenuLabel>
            {ROLE_META.map((r) => {
              const Icon = ROLE_ICON[r.role];
              return (
                <DropdownMenuItem
                  key={r.role}
                  onClick={() => switchRole(r.role)}
                  className="gap-2.5"
                  data-active={r.role === user.role}
                >
                  <Icon className="size-4 text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{r.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{r.demoUser.businessName}</p>
                  </div>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="gap-2.5">
              <LogOut className="size-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {user.role === "reseller" && <CartSheet open={cartOpen} onOpenChange={setCartOpen} />}
    </header>
  );
}

function roleLabel(role: string): string {
  return ROLE_META.find((r) => r.role === role)?.label ?? role;
}
